class SellingPlanPicker extends HTMLElement {
  /** @type {boolean} */
  #ownsSellingPlanInput = false;

  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Parse the per-variant accent map so the harmonic palette can follow the
    // selected flavour's label colour (see #applyVariantAccent).
    this.#parseVariantAccentMap();

    // Cache elements and setup handlers
    this.#initializeElements();
    this.#setupSubHandlers(signal);

    // Listen for variant changes — persistent listener tied to component lifecycle only
    this.#setupVariantChangeListener();

    // Ensure savings badge matches the selected plan on load
    this.#updateSavingsBadgeFromSelection();

    // Play the description enter animation once on first paint (skipped under
    // reduced-motion via the CSS rule). Deferred a frame so initial layout and
    // the collapse-height measurement settle first.
    requestAnimationFrame(() => this.#animateVisibleDescription());
  }

  /** @type {Record<string, string>} */
  #variantAccentMap = {};

  #parseVariantAccentMap() {
    this.#variantAccentMap = {};
    const raw = this.dataset.variantAccentMap;
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        for (const [key, value] of Object.entries(parsed)) {
          if (typeof value === 'string' && value) this.#variantAccentMap[String(key)] = value;
        }
      }
    } catch {
      // Malformed map — leave accent driven by the server-rendered inline var.
    }
  }

  // Point the harmonic palette at a variant's label colour by updating the single
  // --sp-color-accent custom property on the host element. Every derived surface
  // (offer banner, panel, borders, thumb) is a color-mix off this var, so one
  // assignment recolours the whole picker — no per-element updates needed. The
  // host's inline style survives the Section Rendering API content swap, so this
  // holds across flavour changes.
  #applyVariantAccent(variantId) {
    if (variantId == null) return;
    const accent = this.#variantAccentMap[String(variantId)];
    if (accent) this.style.setProperty('--sp-color-accent', accent);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.variantAbortController?.abort();
    this.resizeObserver?.disconnect();

    // Remove the hidden input if this component created it
    if (this.#ownsSellingPlanInput && this.sellingPlanInput?.parentNode) {
      this.sellingPlanInput.remove();
      this.sellingPlanInput = null;
      this.#ownsSellingPlanInput = false;
    }
  }

  #initializeElements() {
    const form =
      this.closest('form[action="/cart/add"]') ||
      document.querySelector('form[action="/cart/add"]:has(input[name="quantity"])');
    this.quantityInput = form ? form.querySelector('input[name="quantity"]') : null;
    this.bulkDiscountOptions = this.querySelectorAll('[data-bulk-discount-option]');
    this.subscriptionRadios = this.querySelectorAll('input[name^="selling-plan-group-"]');
    this.oneTimePurchaseRadio = this.querySelector('.subscription-option--one-time input[type="radio"]');
    this.savingsBadge = this.querySelector('.subscription-option__savings-badge');
    this.sellingPlanInput = this.#setupSellingPlanInput(form);
    this.bulkDiscountWrapper = this.querySelector('.selling-plan-picker__content-wrapper--bulk-discount');
    this.deliveryFrequencyWrapper = this.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency');
    this.deliveryFrequencyRadios = this.querySelectorAll('input[name^="delivery-frequency-"]');

    // Cache the product price display elements (within the same product section)
    const section = this.closest('[data-section-id]') || this.closest('.shopify-section');
    this.priceDisplay = section
      ? section.querySelector('.js-product-price-number')
      : document.querySelector('.js-product-price-number');

    this.priceCompare = section
      ? section.querySelector('.js-product-price-compare')
      : document.querySelector('.js-product-price-compare');

    this.priceText = section
      ? section.querySelector('.js-product-price-text')
      : document.querySelector('.js-product-price-text');

    this.priceSavings = section
      ? section.querySelector('.js-product-price-savings')
      : document.querySelector('.js-product-price-savings');
  }

  #setupSubHandlers(signal) {
    this.#setupHeightTransitions(signal);
    this.#setupBulkDiscountQuantitySync(signal);
    this.#setupDeliveryFrequencyHandlers(signal);
  }

  #setupSellingPlanInput(form) {
    if (!form) return null;

    // Check if input already exists in the form
    const sellingPlanInput = form.querySelector('input[name="selling_plan"][data-selling-plan-input]');

    if (sellingPlanInput) {
      return sellingPlanInput;
    }

    // Input doesn't exist, create it and track ownership for cleanup
    const newInput = document.createElement('input');
    newInput.type = 'hidden';
    newInput.name = 'selling_plan';
    newInput.setAttribute('data-selling-plan-input', '');
    form.appendChild(newInput);
    this.#ownsSellingPlanInput = true;

    return newInput;
  }

  #setupHeightTransitions(signal) {
    let bulkDiscountHeight = 0;
    let deliveryFrequencyHeight = 0;
    let previousWidth = this.offsetWidth;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const measureWrapper = (wrapper, heightVar, opacityVar) => {
      wrapper.style.setProperty(heightVar, 'auto');
      wrapper.style.setProperty(opacityVar, '0');
      void wrapper.offsetHeight;

      const height = Math.ceil(wrapper.scrollHeight);

      wrapper.style.setProperty(heightVar, '0px');
      // Keep opacity at 0 until animation starts to prevent flash
      void wrapper.offsetHeight;

      return height;
    };

    const measureHeights = (updateImmediately = false) => {
      if (this.bulkDiscountWrapper) {
        bulkDiscountHeight = measureWrapper(this.bulkDiscountWrapper, '--wrapper-height', '--wrapper-opacity');
      }

      if (this.deliveryFrequencyWrapper) {
        deliveryFrequencyHeight = measureWrapper(
          this.deliveryFrequencyWrapper,
          '--delivery-frequency-height',
          '--delivery-frequency-opacity',
        );
      }

      if (updateImmediately || prefersReducedMotion) {
        this.#updateContentHeights(bulkDiscountHeight, deliveryFrequencyHeight);
      } else {
        // Use double rAF to ensure 0px state is painted before animating
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#updateContentHeights(bulkDiscountHeight, deliveryFrequencyHeight);
          });
        });
      }
    };

    // Use double requestAnimationFrame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => measureHeights());
    });

    // Update heights when radio buttons change
    for (const radio of this.subscriptionRadios) {
      radio.addEventListener(
        'change',
        () => {
          this.#updateContentHeights(bulkDiscountHeight, deliveryFrequencyHeight);
        },
        { signal },
      );
    }

    // Setup ResizeObserver to update heights on element width changes
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        const currentWidth = entries[0].contentRect.width;

        if (currentWidth !== previousWidth) {
          previousWidth = currentWidth;
          measureHeights(true);
        }
      });

      this.resizeObserver.observe(this);
    }
  }

  #updateContentHeights(bulkDiscountHeight, deliveryFrequencyHeight) {
    const oneTimeChecked = this.oneTimePurchaseRadio?.checked;

    // Update bulk discount wrapper
    if (this.bulkDiscountWrapper) {
      if (oneTimeChecked) {
        // Show bulk discount
        this.bulkDiscountWrapper.style.setProperty('--wrapper-height', `${bulkDiscountHeight}px`);
        this.bulkDiscountWrapper.style.setProperty('--wrapper-opacity', '1');
        this.bulkDiscountWrapper.style.setProperty('--sp-wrapper-translate', '0px');
      } else {
        // Hide bulk discount
        this.bulkDiscountWrapper.style.setProperty('--wrapper-height', '0px');
        this.bulkDiscountWrapper.style.setProperty('--wrapper-opacity', '0');
        this.bulkDiscountWrapper.style.setProperty('--sp-wrapper-translate', '6px');
      }
    }

    // Update delivery frequency wrapper
    if (this.deliveryFrequencyWrapper) {
      if (!oneTimeChecked) {
        // Show delivery frequency
        this.deliveryFrequencyWrapper.style.setProperty('--delivery-frequency-height', `${deliveryFrequencyHeight}px`);
        this.deliveryFrequencyWrapper.style.setProperty('--delivery-frequency-opacity', '1');
        this.deliveryFrequencyWrapper.style.setProperty('--sp-wrapper-translate', '0px');
      } else {
        // Hide delivery frequency
        this.deliveryFrequencyWrapper.style.setProperty('--delivery-frequency-height', '0px');
        this.deliveryFrequencyWrapper.style.setProperty('--delivery-frequency-opacity', '0');
        this.deliveryFrequencyWrapper.style.setProperty('--sp-wrapper-translate', '6px');
      }
    }
  }

  #updateSellingPlan(sellingPlanId) {
    if (this.sellingPlanInput) {
      if (sellingPlanId) {
        this.sellingPlanInput.value = sellingPlanId;
      } else {
        // Remove the value to deselect
        this.sellingPlanInput.value = '';
      }
    }
  }

  #formatSavingsText(percent) {
    const template = this.dataset.savePercentTemplate || 'Save __PERCENT__%';
    return template.replace('__PERCENT__', percent);
  }

  #updateSavingsBadgeFromSelection() {
    if (!this.savingsBadge) return;

    const selectedRadio =
      this.querySelector('input[name^="delivery-frequency-"]:checked') ||
      this.querySelector('input[name^="delivery-frequency-"]');

    if (!selectedRadio) {
      this.savingsBadge.textContent = this.#formatSavingsText(0);
      return;
    }

    const basePrice = parseInt(selectedRadio.dataset.basePrice || '', 10);
    const compareAtPrice = parseInt(selectedRadio.dataset.compareAtPrice || '', 10);
    const adjustedPrice = parseInt(selectedRadio.dataset.adjustedPrice || '', 10);

    const priceBase = Number.isFinite(compareAtPrice) && compareAtPrice > 0 ? compareAtPrice : basePrice;

    if (!Number.isFinite(priceBase) || priceBase <= 0 || !Number.isFinite(adjustedPrice)) {
      this.savingsBadge.textContent = this.#formatSavingsText(0);
      return;
    }

    const savingsPercent = Math.max(0, Math.round(((priceBase - adjustedPrice) / priceBase) * 100));
    this.savingsBadge.textContent = this.#formatSavingsText(savingsPercent);
  }

  #updateQuantity(quantity) {
    if (this.quantityInput) {
      this.quantityInput.value = quantity;
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #updatePriceDisplay() {
    if (!this.priceDisplay) return;

    const variantPrice = parseInt(this.dataset.variantPrice, 10);
    const isOneTime = this.oneTimePurchaseRadio?.checked ?? true;
    let displayPrice;
    let compareAtPrice = null;

    if (isOneTime) {
      const checkedBulk = this.querySelector('[data-bulk-discount-option]:checked');
      const quantity = checkedBulk ? parseInt(checkedBulk.dataset.quantity, 10) : 1;
      const discountedUnitPrice = checkedBulk?.dataset.discountedPrice
        ? parseInt(checkedBulk.dataset.discountedPrice, 10)
        : variantPrice;

      displayPrice = discountedUnitPrice * quantity;
      const fullPrice = variantPrice * quantity;
      compareAtPrice = fullPrice > displayPrice ? fullPrice : null;
    } else {
      const selectedFreq =
        this.querySelector('input[name^="delivery-frequency-"]:checked') ||
        this.querySelector('input[name^="delivery-frequency-"]');

      displayPrice = selectedFreq?.dataset.adjustedPrice
        ? parseInt(selectedFreq.dataset.adjustedPrice, 10)
        : variantPrice;

      const subCompareAt = selectedFreq?.dataset.compareAtPrice
        ? parseInt(selectedFreq.dataset.compareAtPrice, 10)
        : null;
      compareAtPrice = subCompareAt && subCompareAt > displayPrice ? subCompareAt : null;
    }

    if (!Number.isFinite(displayPrice) || displayPrice <= 0) return;

    // Update the main price
    const formatted = Shopify.formatMoney(displayPrice, theme.money_format);
    const moneySpan = this.priceDisplay.querySelector('.money');
    if (moneySpan) {
      moneySpan.textContent = '';
      moneySpan.insertAdjacentHTML('afterbegin', formatted);
    }

    // Toggle sale class on the price number span
    const priceNumberSpan = this.priceDisplay.querySelector(
      '[class*="__price-number"]',
    );
    if (priceNumberSpan) {
      const saleClass = priceNumberSpan.className.replace(/--sale/g, '').trim().split(/\s+/)[0] + '--sale';
      priceNumberSpan.classList.toggle(saleClass, compareAtPrice !== null);
    }

    // Update compare-at price and savings display
    this.#updateCompareAtPrice(compareAtPrice);
    this.#updateSavingsDisplay(compareAtPrice, displayPrice);
  }

  #updateCompareAtPrice(compareAtPrice) {
    if (!this.priceCompare) return;

    if (compareAtPrice) {
      const formattedCompare = Shopify.formatMoney(compareAtPrice, theme.money_format);
      let moneySpan = this.priceCompare.querySelector('.money');

      // If the element was emptied (e.g., by theme.js jQuery .empty()), rebuild inner structure
      if (!moneySpan) {
        const strikeEl = document.createElement('s');
        moneySpan = document.createElement('span');
        moneySpan.className = 'money';
        strikeEl.appendChild(moneySpan);
        this.priceCompare.appendChild(strikeEl);
      }

      moneySpan.textContent = '';
      moneySpan.insertAdjacentHTML('afterbegin', formattedCompare);
      this.priceCompare.dataset.active = '';
    } else {
      delete this.priceCompare.dataset.active;
    }
  }

  #updateSavingsDisplay(compareAtPrice, displayPrice) {
    if (!this.priceSavings) return;

    if (compareAtPrice && compareAtPrice > displayPrice) {
      const savings = compareAtPrice - displayPrice;
      const formattedSavings = Shopify.formatMoney(savings, theme.money_format);
      this.priceSavings.textContent = '';
      this.priceSavings.insertAdjacentHTML('afterbegin', `You save ${formattedSavings}`);
      this.priceSavings.dataset.active = '';
      this.priceSavings.setAttribute('aria-hidden', 'false');
    } else {
      delete this.priceSavings.dataset.active;
      this.priceSavings.setAttribute('aria-hidden', 'true');
    }
  }

  #setupBulkDiscountQuantitySync(signal) {
    // Check if subscription options exist
    const hasSubscriptionOptions = this.subscriptionRadios.length > 0;

    // Handle bulk discount option changes
    for (const option of this.bulkDiscountOptions) {
      option.addEventListener(
        'change',
        () => {
          if (option.checked) {
            // If no subscription options, always update quantity
            // If subscription options exist, only update if one-time purchase is selected
            if (!hasSubscriptionOptions) {
              const quantity = option.dataset.quantity;
              this.#updateQuantity(quantity);
              this.#updatePriceDisplay();
            } else {
              const oneTimePurchase = this.oneTimePurchaseRadio?.checked ? this.oneTimePurchaseRadio : null;
              if (oneTimePurchase) {
                const quantity = option.dataset.quantity;
                this.#updateQuantity(quantity);
                this.#updatePriceDisplay();
              }
            }
          }
        },
        { signal },
      );
    }

    // Handle subscription/one-time purchase radio changes (only if subscription options exist)
    if (hasSubscriptionOptions) {
      for (const radio of this.subscriptionRadios) {
        radio.addEventListener(
          'change',
          () => {
            const oneTimePurchase = this.oneTimePurchaseRadio?.checked ? this.oneTimePurchaseRadio : null;

            if (oneTimePurchase) {
              // One-time purchase selected - clear selling plan and use bulk discount quantity or default to 1
              this.#updateSellingPlan(null);
              const checkedBulkOption = this.querySelector('[data-bulk-discount-option]:checked');
              const quantity = checkedBulkOption ? checkedBulkOption.dataset.quantity : '1';
              this.#updateQuantity(quantity);
            } else {
              // Subscription selected - use checked frequency option's plan ID, or fallback to radio's default
              const checkedFrequencyRadio = this.querySelector('input[name^="delivery-frequency-"]:checked');
              const sellingPlanId = checkedFrequencyRadio
                ? checkedFrequencyRadio.dataset.sellingPlanId
                : radio.dataset.sellingPlanId;
              this.#updateSellingPlan(sellingPlanId);
              this.#updateQuantity('1');
              this.#updateSavingsBadgeFromSelection();
            }
            this.#updatePriceDisplay();
          },
          { signal },
        );
      }
    }

    // Set initial quantity and selling plan on load
    if (hasSubscriptionOptions) {
      const oneTimePurchase = this.oneTimePurchaseRadio?.checked ? this.oneTimePurchaseRadio : null;
      if (oneTimePurchase) {
        // One-time purchase is selected - clear selling plan
        this.#updateSellingPlan(null);
        const checkedBulkOption = this.querySelector('[data-bulk-discount-option]:checked');
        const quantity = checkedBulkOption ? checkedBulkOption.dataset.quantity : '1';
        this.#updateQuantity(quantity);
      } else {
        // Subscription is selected - use checked frequency option's plan ID, or fallback to subscription radio's default
        const checkedFrequencyRadio = this.querySelector('input[name^="delivery-frequency-"]:checked');
        const checkedSubscriptionRadio = this.querySelector('input[name^="selling-plan-group-"]:checked:not([value=""])');

        if (checkedFrequencyRadio) {
          // Use the checked frequency option's plan ID
          const sellingPlanId = checkedFrequencyRadio.dataset.sellingPlanId;
          this.#updateSellingPlan(sellingPlanId);
        } else if (checkedSubscriptionRadio) {
          // Fallback to subscription radio's default plan ID
          const sellingPlanId = checkedSubscriptionRadio.dataset.sellingPlanId;
          this.#updateSellingPlan(sellingPlanId);
        }
        this.#updateQuantity('1');
        this.#updateSavingsBadgeFromSelection();
      }
    } else {
      // No subscription options - clear selling plan and use bulk discount quantity or default to 1
      this.#updateSellingPlan(null);
      const checkedBulkOption = this.querySelector('[data-bulk-discount-option]:checked');
      const quantity = checkedBulkOption ? checkedBulkOption.dataset.quantity : '1';
      this.#updateQuantity(quantity);
    }

    this.#updatePriceDisplay();
  }

  #setupDeliveryFrequencyHandlers(signal) {
    if (this.deliveryFrequencyRadios.length === 0) return;

    // Seed the "last shown description" baseline from the initially-checked plan
    // so the first real change is compared against what the shopper first saw.
    this.#lastDescriptionText = this.#currentDescriptionText();

    for (const radio of this.deliveryFrequencyRadios) {
      radio.addEventListener(
        'change',
        () => {
          if (radio.checked) {
            const sellingPlanId = radio.dataset.sellingPlanId;

            // Update selling plan input
            this.#updateSellingPlan(sellingPlanId);
            this.#updateSavingsBadgeFromSelection();
            this.#updatePriceDisplay();

            // Animate the description only when its copy actually changed. The
            // CSS :has() rule has already swapped which description is visible;
            // we just replay the enter animation on real content changes.
            this.#animateDescriptionIfChanged();
          }
        },
        { signal },
      );
    }
  }

  /** @type {string|null} */
  #lastDescriptionText = null;

  // The currently-visible per-plan description (the one the :has() rule shows
  // for the checked frequency). offsetParent is null under display:none, so the
  // hidden siblings are skipped.
  #visibleDescription() {
    return [...this.querySelectorAll('.subscription-option__description[data-plan-id]')].find(
      (d) => d.offsetParent !== null,
    );
  }

  #descriptionText(el) {
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : null;
  }

  // Snapshot the visible description's copy without animating — used to reset
  // the baseline (first paint, variant patch) so the NEXT change is compared
  // against what the shopper currently sees.
  #currentDescriptionText() {
    return this.#descriptionText(this.#visibleDescription());
  }

  // Play the enter animation on the visible description unconditionally, and set
  // the baseline. Used on first paint.
  #animateVisibleDescription() {
    const visible = this.#visibleDescription();
    if (!visible) return;
    this.#lastDescriptionText = this.#descriptionText(visible);
    this.#retriggerDescriptionAnimation(visible);
  }

  // Replay the enter animation on the visible description, but only if its copy
  // differs from the previously-shown one. Identical copy (the common case
  // across frequencies) produces no motion.
  #animateDescriptionIfChanged() {
    const visible = this.#visibleDescription();
    if (!visible) return;

    const text = this.#descriptionText(visible);
    if (text === this.#lastDescriptionText) return;
    this.#lastDescriptionText = text;
    this.#retriggerDescriptionAnimation(visible);
  }

  // Play the enter animation as a strict one-shot. The class must NOT persist:
  // if it did, the CSS animation would replay every time the CSS :has() rule
  // re-shows this description (display:none -> block), flashing it on switches
  // back to an already-seen option. So we remove it on animationend (and up
  // front, to retrigger cleanly if it's still mid-animation).
  #retriggerDescriptionAnimation(el) {
    el.classList.remove('is-changed');
    void el.offsetWidth;
    el.classList.add('is-changed');
    el.addEventListener('animationend', () => el.classList.remove('is-changed'), { once: true });
  }

  #setupVariantChangeListener() {
    // Use a separate AbortController so this listener persists across re-initializations
    this.variantAbortController = new AbortController();

    // The Venue theme dispatches "venue:variant:update" on `document` with
    // { product, variant, sectionClass }. Listen there to detect variant changes.
    document.addEventListener(
      'venue:variant:update',
      (event) => {
        const { product, variant } = event.detail ?? {};
        if (!variant || !product) return;

        // Only handle events for the same product
        if (String(product.id) !== this.dataset.productId) return;

        // Recolour the harmonic palette to the new flavour's label colour
        // immediately, so the swap reads as instant even before the fetch lands.
        this.#applyVariantAccent(variant.id);

        // Resolve the section ID from the closest section element
        const sectionEl = this.closest('[data-section-id]');
        if (!sectionEl) return;
        const sectionId = sectionEl.dataset.sectionId;

        // Use the Section Rendering API to fetch fresh HTML with the new variant
        const url = `${window.location.pathname}?variant=${variant.id}&section_id=${sectionId}`;

        fetch(url)
          .then((res) => {
            if (!res.ok) return;
            return res.text();
          })
          .then((html) => {
            if (!html) return;

            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newPicker = doc.querySelector('selling-plan-picker');
            if (!newPicker) return;

            const newContent = newPicker.querySelector('.selling-plan-picker__content');
            if (!newContent) return;

            // Patch the live DOM in place with the new variant's data instead of
            // swapping the whole content node. Replacing would tear down every
            // element — flickering the card, restarting the collapse animation,
            // and re-running connectedCallback-style setup — which reads as the
            // widget "refreshing/re-opening" on each flavour/size change. By
            // mutating only the per-variant values (prices, plan ids, savings,
            // descriptions) the structure, listeners, and the shopper's current
            // selection all stay intact, so the change is seamless.
            if (newPicker.dataset.variantPrice) {
              this.dataset.variantPrice = newPicker.dataset.variantPrice;
            }

            this.#patchVariantContent(newContent);

            // Reflect the patched values without re-initializing: refresh the
            // savings badge and the product price display from the live DOM.
            this.#updateSavingsBadgeFromSelection();
            this.#updatePriceDisplay();

            // The patch rewrote the description copy in place. Re-baseline
            // WITHOUT animating so the seamless variant swap stays motionless;
            // a subsequent frequency change still animates only on real change.
            this.#lastDescriptionText = this.#currentDescriptionText();
          })
          .catch(() => {
            // Silently fail — the picker keeps displaying the previous variant's plans
          });
      },
      { signal: this.variantAbortController.signal },
    );
  }

  // Copy the per-variant values from a freshly server-rendered content fragment
  // into the live DOM without replacing any nodes. The set of plans and bulk
  // tiers is identical across a product's variants (only their prices and
  // allocations differ), so every node is matched by a stable key — plan id for
  // subscription rows, positional index for bulk tiers — and only its text and
  // data-* values are updated. Nothing is added, removed, or re-created, so the
  // widget never re-opens and all existing listeners keep working.
  #patchVariantContent(newContent) {
    const currentContent = this.querySelector('.selling-plan-picker__content');
    if (!currentContent) return;

    // --- Per-plan subscription prices (keyed by data-plan-price) ---
    for (const newPrice of newContent.querySelectorAll('.subscription-option__price[data-plan-price]')) {
      const planId = newPrice.dataset.planPrice;
      const currentPrice = currentContent.querySelector(
        `.subscription-option__price[data-plan-price="${CSS.escape(planId)}"]`,
      );
      if (currentPrice) currentPrice.innerHTML = newPrice.innerHTML;
    }

    // --- One-time price ---
    const newOneTime = newContent.querySelector('.subscription-option__price--one-time');
    const currentOneTime = currentContent.querySelector('.subscription-option__price--one-time');
    if (newOneTime && currentOneTime) currentOneTime.innerHTML = newOneTime.innerHTML;

    // --- Delivery-frequency radios (keyed by data-selling-plan-id) ---
    // Update the pricing data-* the price/savings logic reads. Leave `checked`
    // untouched so the shopper's chosen frequency is preserved.
    for (const newRadio of newContent.querySelectorAll('input[name^="delivery-frequency-"]')) {
      const planId = newRadio.dataset.sellingPlanId;
      const currentRadio = currentContent.querySelector(
        `input[name^="delivery-frequency-"][data-selling-plan-id="${CSS.escape(planId)}"]`,
      );
      if (!currentRadio) continue;
      for (const attr of ['data-adjusted-price', 'data-compare-at-price', 'data-base-price']) {
        const value = newRadio.getAttribute(attr);
        if (value != null) currentRadio.setAttribute(attr, value);
      }
    }

    // --- Per-plan descriptions (keyed by data-plan-id) ---
    for (const newDesc of newContent.querySelectorAll('.subscription-option__description[data-plan-id]')) {
      const planId = newDesc.dataset.planId;
      const currentDesc = currentContent.querySelector(
        `.subscription-option__description[data-plan-id="${CSS.escape(planId)}"]`,
      );
      if (currentDesc) currentDesc.innerHTML = newDesc.innerHTML;
    }

    // --- Bulk-discount tiers (keyed by position) ---
    // Quantity structure is fixed per product; only the discounted price changes.
    const newBulk = [...newContent.querySelectorAll('[data-bulk-discount-option]')];
    const currentBulk = [...currentContent.querySelectorAll('[data-bulk-discount-option]')];
    newBulk.forEach((newOption, index) => {
      const currentOption = currentBulk[index];
      if (!currentOption) return;
      if (newOption.dataset.discountedPrice != null) {
        currentOption.dataset.discountedPrice = newOption.dataset.discountedPrice;
      }
      const newLabel = newOption.closest('.bulk-discount-option');
      const currentLabel = currentOption.closest('.bulk-discount-option');
      const newPriceEl = newLabel?.querySelector('.bulk-discount-option__price');
      const currentPriceEl = currentLabel?.querySelector('.bulk-discount-option__price');
      if (newPriceEl && currentPriceEl) currentPriceEl.innerHTML = newPriceEl.innerHTML;
    });

    // --- First-order offer banner savings copy ---
    const newOfferCopy = newContent.querySelector('.subscription-options__offer-copy');
    const currentOfferCopy = currentContent.querySelector('.subscription-options__offer-copy');
    if (newOfferCopy && currentOfferCopy) currentOfferCopy.textContent = newOfferCopy.textContent;
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
